<script setup>
import { ref, computed } from "vue";
import { solutionsArray } from "@/data/solutions.js";
import {
    PlusIcon,
    CloseIcon,
    RoundedWebsiteIcon,
    RoundedFacebookIcon,
    RoundedInstagramIcon,
    RoundedWhatsAppIcon,
    RoundedViberIcon,
    RoundedShopifyIcon,
    RoundedCloudIcon
} from "@/components/icons";

// Lottie Animation
import { LottieAnimation } from "lottie-web-vue";
import solutionsAnimation from "/public/images/lottie/solutions.json";
import mobileAnimation from "/public/images/lottie/phone.json";

// Swiper
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// Particle JS
import { loadSlim } from "tsparticles-slim";

const particlesInit = async engine => {
    await loadSlim(engine);
};

const options = computed(() => {
    return [
    {
        fpsLimit: 120,
        fullScreen: {
            enable: false
        },
        particles: {
            color: {
                value: '#ffffff'
            },
            links: {
                color: '#ffffff',
                distance: 150,
                enable: true,
                opacity: 0.5,
                width: 1
            },
            collisions: {
                enable: true
            },
            move: {
                direction: 'none',
                enable: true,
                outModes: {
                    default: 'bounce'
                },
                random: false,
                speed: 1,
                straight: false
            },
            number: {
                density: {
                    enable: true,
                    area: 800
                },
                value: 100
            },
            opacity: {
                value: 0.5
            },
            shape: {
                type: 'circle'
            },
            size: {
                value: { min: 1, max: 5 },
            }
        },
        detectRetina: true
    }]
});

const solutions = solutionsArray();

const selectedSection = ref([]);

const isExpanded = (sectionId) => {
    setTimeout(() => {
    }, 1000);
    return selectedSection.value.includes(sectionId);
}

const expandCollapseItem = (sectionId) => {
    if(selectedSection.value.includes(sectionId)){
        const index = selectedSection.value.indexOf(sectionId);
        selectedSection.value.splice(index, 1);
        return;
    }
    selectedSection.value.push(sectionId);
}

const Integrations = [
    { id: 1, icon: RoundedWebsiteIcon },
    { id: 2, icon: RoundedFacebookIcon },
    { id: 3, icon: RoundedInstagramIcon },
    { id: 4, icon: RoundedWhatsAppIcon },
    { id: 5, icon: RoundedViberIcon },
    { id: 6, icon: RoundedShopifyIcon },
    { id: 7, icon: RoundedCloudIcon }
];
</script>

<template>
    <div class="flex flex-col">
        <!-- Home Banner -->
        <swiper
            :slides-per-view="1"
            :autoplay="{
                delay: 5000,
                disableOnInteraction: false,
            }"
            :loop="true"
            :pagination="true"
            class="w-full mt-[7rem]"
        >
            <swiper-slide v-for="banner in solutions.banners" :key="banner.id">
                <div 
                    :style="`background: url('images/homepage/${banner.file}') no-repeat; background-size: cover; background-position: center;`" 
                    :class="[banner.position, `relative flex items-center gap-6 w-full 2xl:h-[900px] lg:h-[750px]`]"
                >
                    <div class="flex flex-col justify-center md:text-center md:gap-10 gap-8 2xl:max-w-[50rem] max-w-[55rem] max-sm:p-6">
                        <div :class="[banner.titleStyle, `leading-tight font-extrabold 2xl:text-[50px] lg:text-[40px] text-[35px]`]">
                            {{ banner.title }}
                        </div>
                        <div :class="[banner.descriptionStyle, `font-extrabold 2xl:text-[35px] lg:text-[29px] text-[22px]`]">
                            {{ banner.description }}
                        </div>
                        <h1 
                            v-if="banner.actionText"
                            :class="[banner.actionTextStyle, `2xl:text-[22px] text-[20px]`]"
                        >
                            {{ banner.actionText }}
                        </h1>
                        <img 
                            v-if="banner.actionImage"
                            :src="`images/homepage/${banner.actionImage}`"
                        >
                        <div 
                            v-if="banner.buttonImage"
                            class="flex justify-center"
                        >
                            <a href="https://app.smicos.com/register" target="_blank">
                                <img
                                    :src="`images/homepage/${banner.buttonImage}`"
                                    alt="banner button"
                                >
                            </a>
                        </div>
                        <!-- <a 
                            v-if="banner.id === 1"
                            href="https://app.smicos.com/register" 
                            class="flex justify-center" 
                            :class="`relative z-[${bannerZIndex}]`" target="_blank"
                        >
                            <span
                                class="bg-[#E1251B] hover:bg-[#E1251B]/70 rounded-full px-8 py-4 text-[18px] text-white font-semibold cursor-pointer"
                                :class="`relative z-[${bannerZIndex}]`"
                            >
                                Get Smicos for <span class="font-bold">FREE</span>
                            </span>
                        </a>  -->
                    </div>
                </div>
            </swiper-slide>
        </swiper>

        <!-- Feature Section -->
        <div class="relative flex md:flex-row flex-col items-center max-sm:gap-4 bg-gradient-to-r from-[#0A9EDF] to-[#31549D] p-8 z-[2]">
            <div class="absolute top-0 right-0 w-[50rem] h-full">
                <vue-particles
                    id="tsparticles"
                    :particlesInit="particlesInit"
                    :options="options"
                    style="height: 100%;"
                />
            </div>
            <div class="md:hidden block flex-1">
                <LottieAnimation
                    :animation-data="mobileAnimation"
                    :auto-play="true"
                    :loop="true"
                    :speed="1"
                    class="md:h-[40rem] h-[20rem]"
                />
            </div>
            <div class="flex-1 flex flex-col gap-10 2xl:pl-20">
                <h2 class="text-white text-center font-bold lg:text-[55px] md:text-[45px] text-[35px]">
                    We Understand<br> Your Business <span class="bg-gradient-to-r from-[#E37C0D] to-[#F2CD59] bg-clip-text text-transparent font-extrabold">Needs</span>
                </h2>
                <LottieAnimation
                    :animation-data="solutionsAnimation"
                    :auto-play="true"
                    :loop="true"
                    :speed="0.5"  
                />
                <!-- <div class="flex flex-wrap gap-14 items-center">
                    <div
                        v-for="solution in solutions"
                        class="flex items-center gap-1 md:w-[40%] w-full"
                    >
                        <component :is="solution.icon" class="w-7"/>
                        <div class="flex items-center">
                            <div class="w-5 h-5 bg-[#E37C0D] rounded-full solutions-bullet"></div>
                            <span class="relative -left-2 text-white font-bold">
                                {{ solution.title }}
                            </span>
                        </div>
                    </div>
                </div> -->
            </div>
            <div class="md:block hidden flex-1 p-10">
                <LottieAnimation
                    :animation-data="mobileAnimation"
                    :auto-play="true"
                    :loop="true"
                    :speed="1"
                    class="lg:h-[40rem]"
                />
            </div>
        </div>

        <!-- Smicos Game Changer -->
        <div class="flex justify-center text-center w-full py-10">
            <div class="flex flex-col w-full max-w-[80%]">
                <h1 class="font-extrabold lg:text-[45px] md:text-[40px] text-[30px] bg-gradient-to-r from-[#E37C0D] to-[#F2CD59] bg-clip-text text-transparent">
                    Smicos Game Changer for AI-driven Wins
                </h1>
                <div class="flex xl:flex-row flex-col items-center justify-center 2xl:gap-0 gap-4 w-full 2xl:px-20 2xl:py-10 py-4">
                    <div class="flex flex-col items-center justify-center gap-4 bg-[#77777A]/10 min-w-[18rem] max-w-[18rem] h-full p-10 rounded-xl">
                        <svg width="65" height="66" viewBox="0 0 65 66" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22.3438 27.9216H10.1562C9.34817 27.9216 8.57318 27.6005 8.00178 27.0292C7.43038 26.4578 7.10938 25.6828 7.10938 24.8747V12.6872C7.10938 11.8791 7.43038 11.1041 8.00178 10.5327C8.57318 9.96132 9.34817 9.64031 10.1562 9.64031C10.9643 9.64031 11.7393 9.96132 12.3107 10.5327C12.8821 11.1041 13.2031 11.8791 13.2031 12.6872V17.5114L15.1836 15.5309C19.9055 10.79 26.3166 8.11787 33.0078 8.10164H33.15C39.7838 8.08555 46.1571 10.6825 50.8904 15.3303C51.1765 15.6101 51.4047 15.9435 51.562 16.3114C51.7192 16.6793 51.8024 17.0746 51.8069 17.4748C51.8114 17.8749 51.737 18.2719 51.5881 18.6433C51.4391 19.0147 51.2184 19.353 50.9387 19.6391C50.6589 19.9252 50.3256 20.1534 49.9576 20.3107C49.5897 20.4679 49.1944 20.5512 48.7943 20.5556C48.3942 20.5601 47.9971 20.4857 47.6257 20.3368C47.2544 20.1878 46.916 19.9671 46.6299 19.6874C42.9968 16.1392 38.1115 14.1662 33.0334 14.1961C27.9552 14.226 23.0935 16.2566 19.5025 19.8473L17.5195 21.8278H22.3438C23.1518 21.8278 23.9268 22.1488 24.4982 22.7202C25.0696 23.2916 25.3906 24.0666 25.3906 24.8747C25.3906 25.6828 25.0696 26.4578 24.4982 27.0292C23.9268 27.6005 23.1518 27.9216 22.3438 27.9216ZM54.8438 38.0778H42.6562C41.8482 38.0778 41.0732 38.3988 40.5018 38.9702C39.9304 39.5416 39.6094 40.3166 39.6094 41.1247C39.6094 41.9328 39.9304 42.7077 40.5018 43.2791C41.0732 43.8505 41.8482 44.1716 42.6562 44.1716H47.4805L45.5 46.152C41.9144 49.7554 37.0451 51.7882 31.9617 51.804H31.8525C26.8119 51.817 21.969 49.8439 18.3727 46.312C17.7949 45.747 17.0163 45.4347 16.2083 45.4437C15.4002 45.4528 14.6289 45.7825 14.0639 46.3602C13.4989 46.938 13.1866 47.7166 13.1956 48.5246C13.2047 49.3327 13.5343 50.104 14.1121 50.669C18.8455 55.3169 25.2188 57.9138 31.8525 57.8977H31.9922C38.681 57.8768 45.088 55.202 49.8063 50.4608L51.7969 48.488V53.3122C51.7969 54.1203 52.1179 54.8952 52.6893 55.4666C53.2607 56.038 54.0357 56.3591 54.8438 56.3591C55.6518 56.3591 56.4268 56.038 56.9982 55.4666C57.5696 54.8952 57.8906 54.1203 57.8906 53.3122V41.1247C57.8906 40.3166 57.5696 39.5416 56.9982 38.9702C56.4268 38.3988 55.6518 38.0778 54.8438 38.0778Z" fill="#1E1E1E"/>
                        </svg>
                        <span class="font-semibold text-[#8F8F8F]">
                            your conversion machine
                        </span>
                    </div>
                    <div class="bg-smicos-secondary rounded-full p-3">
                        <PlusIcon class="w-8 text-white"/>
                    </div>
                    <div class="flex flex-col items-center justify-center gap-4 bg-[#77777A]/10 min-w-[18rem] max-w-[18rem] h-full p-10 rounded-xl">
                        <svg width="149" height="61" viewBox="0 0 149 61" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16.901 15.392C13.445 15.392 11.077 17.312 7.749 21.408L0.837 15.84C5.253 9.824 9.541 6.56 17.541 6.56C27.077 6.56 32.965 12.064 32.965 20.448C32.965 27.936 29.125 31.712 21.189 37.856L13.893 43.488H33.413V52H0.261V44.192L15.173 31.968C20.741 27.36 22.917 24.928 22.917 21.216C22.917 17.44 20.421 15.392 16.901 15.392ZM62.6225 6.88H70.9425V34.336H76.9585V42.336H70.9425V52H61.5345V42.336H39.6465L38.0465 35.36L62.6225 6.88ZM61.5345 34.336V20.384L49.6305 34.336H61.5345ZM105.498 0.927998H113.754L85.4015 60.192H77.1455L105.498 0.927998ZM116.602 15.712V7.2H148.858V14.624L128.762 52H117.626L137.658 15.712H116.602Z" fill="#1E1E1E"/>
                        </svg>
                        <span class="font-semibold text-[#8F8F8F]">
                            common questions answered in real-time
                        </span>
                    </div>
                    <div class="bg-smicos-secondary rounded-full p-3">
                        <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M29 20.5C29 20.8978 28.842 21.2794 28.5607 21.5607C28.2794 21.842 27.8978 22 27.5 22H5.5C5.10218 22 4.72064 21.842 4.43934 21.5607C4.15804 21.2794 4 20.8978 4 20.5C4 20.1022 4.15804 19.7206 4.43934 19.4393C4.72064 19.158 5.10218 19 5.5 19H27.5C27.8978 19 28.2794 19.158 28.5607 19.4393C28.842 19.7206 29 20.1022 29 20.5ZM5.5 14H27.5C27.8978 14 28.2794 13.842 28.5607 13.5607C28.842 13.2794 29 12.8978 29 12.5C29 12.1022 28.842 11.7206 28.5607 11.4393C28.2794 11.158 27.8978 11 27.5 11H5.5C5.10218 11 4.72064 11.158 4.43934 11.4393C4.15804 11.7206 4 12.1022 4 12.5C4 12.8978 4.15804 13.2794 4.43934 13.5607C4.72064 13.842 5.10218 14 5.5 14Z" fill="white"/>
                        </svg>
                    </div>
                    <div class="flex flex-col items-center justify-center gap-4 bg-[#77777A]/10 min-w-[18rem] max-w-[18rem] h-full p-10 rounded-xl">
                        <svg width="172" height="47" viewBox="0 0 172 47" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0.25475 4.72L13.1188 0.879997H19.8388V46H10.1108V10.864L2.30275 12.784L0.25475 4.72ZM68.9828 23.536C68.9828 36.592 60.9827 46.768 49.0148 46.768C37.0468 46.768 29.1748 36.72 29.1748 23.664C29.1748 10.608 37.1748 0.431999 49.1428 0.431999C61.1108 0.431999 68.9828 10.48 68.9828 23.536ZM58.8708 23.664C58.8708 15.472 54.9668 9.328 49.0148 9.328C43.0628 9.328 39.2868 15.28 39.2868 23.536C39.2868 31.792 43.1908 37.872 49.1428 37.872C55.0948 37.872 58.8708 31.856 58.8708 23.664ZM115.733 23.536C115.733 36.592 107.733 46.768 95.7648 46.768C83.7968 46.768 75.9248 36.72 75.9248 23.664C75.9248 10.608 83.9248 0.431999 95.8928 0.431999C107.861 0.431999 115.733 10.48 115.733 23.536ZM105.621 23.664C105.621 15.472 101.717 9.328 95.7648 9.328C89.8128 9.328 86.0368 15.28 86.0368 23.536C86.0368 31.792 89.9408 37.872 95.8928 37.872C101.845 37.872 105.621 31.856 105.621 23.664ZM143.859 12.08C143.859 18.48 139.443 23.6 132.915 23.6C126.451 23.6 122.099 18.544 122.099 12.208C122.099 5.808 126.515 0.688 133.043 0.688C139.507 0.688 143.859 5.744 143.859 12.08ZM166.899 1.2L134.195 46H126.579L159.283 1.2H166.899ZM171.379 34.992C171.379 41.392 166.963 46.512 160.435 46.512C153.971 46.512 149.619 41.456 149.619 35.12C149.619 28.72 154.035 23.6 160.563 23.6C167.027 23.6 171.379 28.656 171.379 34.992ZM137.139 12.208C137.139 9.136 135.475 6.512 132.915 6.512C130.227 6.512 128.819 9.072 128.819 12.08C128.819 15.152 130.483 17.776 133.043 17.776C135.731 17.776 137.139 15.216 137.139 12.208ZM164.659 35.12C164.659 32.048 162.995 29.424 160.435 29.424C157.747 29.424 156.339 31.984 156.339 34.992C156.339 38.064 158.003 40.688 160.563 40.688C163.251 40.688 164.659 38.128 164.659 35.12Z" fill="#E1251B"/>
                        </svg>
                        <span class="font-semibold text-[#8F8F8F]">
                            smart analytics for better decision making
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Transform -->
        <div class="flex flex-col items-center">
            <!-- Smicos Dashboard -->
            <div class="flex flex-col gap-10 w-full md:py-[2.5rem] p-[1rem] bg-gradient-to-r from-[#F2CD59] to-[#E37C0D]">
                <h2 class="lg:text-[45px] md:text-[40px] text-[30px] font-extrabold text-center text-[#FFFFFF] leading-tight">
                    Transform your business operations with Smicos chatbot solutions.
                </h2>
                <div class="flex justify-center">
                    <div class="flex xl:flex-row flex-col justify-between 2xl:gap-0 md:gap-10 gap-4 w-full xl:max-w-[95%] md:max-w-[80%]">
                        <div class="flex flex-col gap-4 2xl:w-[55%] w-full">
                            <span class="font-bold md:text-[30px] text-[24px] max-sm:text-center text-white">
                                Smicos Dashboard & Reports
                            </span>
                            <img
                                :src="`/images/solutions/sections/gif/dashboard.gif`"
                                class="w-full"
                                alt="smicos dashboard"
                            >
                        </div>
                        <div class="2xl:w-[40%] w-full">
                            <div class="flex flex-col gap-2 md:mt-[5rem]">
                                <span class="md:text-[20px] text-[18px] font-extrabold text-white pb-[1rem] uppercase">
                                    Unlock valuable insights into how your audience interacts with your support system.
                                </span>
                                <div class="flex flex-col gap-2 xl:h-[32rem] md:h-[25rem] h-[20rem] custom-scrollbar overflow-y-auto md:pr-[0.5rem]">
                                    <template v-for="section in solutions.sections" :key="section.id">
                                        <div
                                            v-if="section.section == `dashboard`"
                                            :class="!isExpanded(section.id) && `hover:bg-[#f2cd59]`"
                                        >
                                            <div
                                                @click="expandCollapseItem(section.id)"
                                                class="flex flex-col gap-4 border-[1px] border-[#FFF] md:p-[1rem] p-[0.5rem] cursor-pointer text-white transition-all duration-500"
                                                :class="isExpanded(section.id) ? `md:h-[10rem] h-[15rem] bg-[#D78924]` : `h-[4rem]`"
                                            >
                                                <div class="flex items-start md:gap-6 gap-1">
                                                    <div class="md:w-6 w-5">
                                                        <PlusIcon
                                                            v-if="!isExpanded(section.id)"
                                                            class="md:w-6 w-5"
                                                        />
                                                        <CloseIcon
                                                           v-if="isExpanded(section.id)"
                                                            class="md:w-6 w-5"
                                                        />
                                                    </div>
                                                    <div class="flex flex-col md:gap-4 gap-1">
                                                        <span class="font-bold md:text-xl leading-tight">
                                                            {{ section.name }}
                                                        </span>
                                                        <span
                                                            class="text-justify transition-all duration-500 max-sm:pr-[1rem]"
                                                            :class="isExpanded(section.id) ? `opacity-1 pointer-events-auto` : `opacity-0 pointer-events-none`"
                                                        >
                                                            {{ section.description }}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Smicos Story Maker -->
            <div class="flex xl:flex-row flex-col justify-between md:py-[2.5rem] p-[1rem] 2xl:gap-0 md:gap-10 gap-4 w-full xl:max-w-[95%] md:max-w-[80%]">
                <div class="2xl:w-[40%] w-full">
                    <div class="flex flex-col gap-2">
                        <span class="md:text-[20px] text-[18px] font-extrabold pb-4 uppercase">
                            Keep customers hooked with engaging bot responses. Your automation tool for the most critical point of customer journey.
                        </span>
                        <div class="flex flex-col gap-2 xl:h-[32rem] md:h-[25rem] h-[20rem] custom-scrollbar overflow-y-auto md:pr-[0.5rem]">
                            <template v-for="section in solutions.sections" :key="section.id">
                                <div 
                                    v-if="section.section == `story-maker`" 
                                    :class="!isExpanded(section.id) && `hover:bg-[#e37c0d] hover:text-white`"
                                >
                                    <div
                                        @click="expandCollapseItem(section.id)"
                                        class="flex flex-col gap-4 border-[1px] border-[#000] md:p-[1rem] p-[0.5rem] cursor-pointer transition-all duration-500"
                                        :class="isExpanded(section.id) ? `md:h-[10rem] h-[15rem] bg-[#D78924] text-white` : `h-[4rem]`"
                                    >
                                        <div class="flex items-start md:gap-6 gap-1">
                                            <div class="w-6">
                                                <PlusIcon 
                                                    v-if="!isExpanded(section.id)"
                                                    class="md:w-6 w-5"
                                                />
                                                <CloseIcon 
                                                   v-if="isExpanded(section.id)"
                                                    class="md:w-6 w-5"
                                                />
                                            </div>
                                            <div class="flex flex-col md:gap-4 gap-1">
                                                <span class="font-bold md:text-xl leading-tight">
                                                    {{ section.name }}
                                                </span>
                                                <span 
                                                    class="text-justify max-sm:pr-[1rem]"
                                                    :class="isExpanded(section.id) ? `opacity-1 pointer-events-auto` : `opacity-0 pointer-events-none`"
                                                >
                                                    {{ section.description }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col gap-4 2xl:w-[55%] w-full">
                    <span class="font-bold md:text-[30px] text-[24px] md:text-right leading-tight">
                        Story Maker & Story Training
                    </span>
                    <!-- <video
                        autoplay loop muted
                        class="w-full shadow-lg border rounded-md"
                    >
                        <source :src="`/images/solutions/sections/story-maker.webm`" type="video/mp4">
                    </video> -->
                    <img
                        :src="`/images/solutions/sections/gif/story-maker.gif`"
                        class="w-full"
                        alt="story maker"
                    >
                </div>
            </div>
            <!-- Smicos Customer Database -->
            <div class="flex justify-center w-full md:py-[2.5rem] p-[1rem] bg-gradient-to-r from-[#F2CD59] to-[#E37C0D]">
                <div class="flex xl:flex-row flex-col justify-between 2xl:gap-0 md:gap-10 gap-4 w-full xl:max-w-[95%] md:max-w-[80%]">
                    <div class="flex flex-col gap-4 2xl:w-[55%] w-full">
                        <span class="font-bold md:text-[30px] text-[24px] text-white max-sm:text-center leading-tight">
                            Customer Database
                        </span>
                        <img
                            :src="`/images/solutions/sections/gif/customer-database.gif`"
                            class="w-full"
                            alt="customer database"
                        >
                    </div>
                    <div class="2xl:w-[40%] w-full">
                        <div class="flex flex-col gap-2 md:mt-[5rem]">
                            <span class="md:text-[20px] text-[18px] font-extrabold text-white pb-4 uppercase">
                                A comprehensive leads database in one platform, enabling your sales team to focus on the most promising leads for faster conversions.
                            </span>
                            <div class="flex flex-col gap-2 xl:h-[32rem] md:h-[25rem] h-[20rem] custom-scrollbar overflow-y-auto md:pr-[0.5rem]">
                                <template v-for="section in solutions.sections" :key="section.id">
                                    <div
                                        v-if="section.section == `customer-database`"
                                        :class="!isExpanded(section.id) && `hover:bg-[#f2cd59]`"
                                    >
                                        <div
                                            @click="expandCollapseItem(section.id)"
                                            class="flex flex-col gap-4 border-[1px] border-[#FFF] md:p-[1rem] p-[0.5rem] cursor-pointer text-white transition-all duration-500"
                                            :class="isExpanded(section.id) ? `md:h-[10rem] h-[15rem] bg-[#D78924]` : `h-[4rem]`"
                                        >
                                            <div class="flex items-start md:gap-6 gap-1">
                                                <div class="md:w-6 w-5">
                                                    <PlusIcon
                                                        v-if="!isExpanded(section.id)"
                                                        class="md:w-6 w-5"
                                                    />
                                                    <CloseIcon
                                                       v-if="isExpanded(section.id)"
                                                        class="md:w-6 w-5"
                                                    />
                                                </div>
                                                <div class="flex flex-col gap-4">
                                                    <span class="font-bold md:text-xl leading-tight">
                                                        {{ section.name }}
                                                    </span>
                                                    <span
                                                        class="text-justify transition-all duration-500 max-sm:pr-[1rem]"
                                                        :class="isExpanded(section.id) ? `opacity-1 pointer-events-auto` : `opacity-0 pointer-events-none`"
                                                    >
                                                        {{ section.description }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Smicos Ticketing Tool -->
            <div class="flex xl:flex-row flex-col justify-between md:py-[2.5rem] p-[1rem] 2xl:gap-0 md:gap-10 gap-4 w-full xl:max-w-[95%] md:max-w-[80%]">
                <div class="2xl:w-[40%] w-full">
                    <div class="flex flex-col gap-2 md:mt-[5rem]">
                        <span class="md:text-[20px] text-[18px] font-extrabold pb-[1rem] uppercase">
                            Effortlessly manage, assign, prioritize, and respond to customer requests, all in one place.
                        </span>
                        <div class="flex flex-col gap-2 xl:h-[32rem] md:h-[25rem] h-[15rem] custom-scrollbar overflow-y-auto md:pr-[0.5rem]">
                            <template v-for="section in solutions.sections" :key="section.id">
                                <div 
                                    v-if="section.section == `ticketing`" 
                                    :class="!isExpanded(section.id) && `hover:bg-[#e37c0d] hover:text-white`"
                                >
                                    <div
                                        @click="expandCollapseItem(section.id)"
                                        class="flex flex-col gap-4 border-[1px] border-[#000] p-4 cursor-pointer transition-all duration-500"
                                        :class="isExpanded(section.id) ? `md:h-[10rem] h-[12rem] bg-[#D78924] text-white` : `h-[4rem]`"
                                    >
                                        <div class="flex items-start md:gap-6 gap-1">
                                            <div class="md:w-6 w-5">
                                                <PlusIcon 
                                                    v-if="!isExpanded(section.id)"
                                                    class="md:w-6 w-5"
                                                />
                                                <CloseIcon 
                                                   v-if="isExpanded(section.id)"
                                                    class="md:w-6 w-5"
                                                />
                                            </div>
                                            <div class="flex flex-col md:gap-4 gap-2">
                                                <span class="font-bold md:text-xl leading-tight">
                                                    {{ section.name }}
                                                </span>
                                                <span 
                                                    class="text-justify max-sm:pr-[1rem]"
                                                    :class="isExpanded(section.id) ? `opacity-1 pointer-events-auto` : `opacity-0 pointer-events-none`"
                                                >
                                                    {{ section.description }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col gap-4 2xl:w-[55%] w-full">
                    <span class="font-bold md:text-[30px] text-[24px] text-right max-sm:text-center leading-tight">
                        Ticketing System
                    </span>
                    <img
                        :src="`/images/solutions/sections/gif/ticketing-system.gif`"
                        class="w-full"
                        alt="smicos dashboard"
                    >
                </div>
            </div>
            <!-- Smicos Live Agent -->
            <div class="flex justify-center w-full md:py-[2.5rem] p-[1rem] bg-gradient-to-r from-[#F2CD59] to-[#E37C0D]">
                <div class="flex xl:flex-row flex-col justify-between 2xl:gap-0 md:gap-10 gap-4 w-full xl:max-w-[95%] md:max-w-[80%]">
                    <div class="flex flex-col gap-4 2xl:w-[55%] w-full">
                        <span class="font-bold md:text-[30px] text-[24px] max-sm:text-center text-white leading-tight">
                            Live Agent
                        </span>
                        <img
                            :src="`/images/solutions/sections/gif/live-agent.gif`"
                            class="w-full"
                            alt="smicos dashboard"
                        >
                    </div>
                    <div class="2xl:w-[40%] w-full md:mt-[5rem]">
                        <div class="flex flex-col gap-2">
                            <span class="md:text-[20px] text-[18px] font-extrabold text-white pb-4 uppercase">
                                Allow Smicos to seamlessly route chats to your customer support team as needed.
                            </span>
                            <div class="flex flex-col gap-2 xl:h-[32rem] md:h-[25rem] h-[15rem] custom-scrollbar overflow-y-auto pr-2">
                                <template v-for="section in solutions.sections" :key="section.id">
                                    <div
                                        v-if="section.section == `live-agent`"
                                        :class="!isExpanded(section.id) && `hover:bg-[#f2cd59]`"
                                    >
                                        <div
                                            @click="expandCollapseItem(section.id)"
                                            class="flex flex-col gap-4 border-[1px] border-[#FFF] p-4 cursor-pointer text-white transition-all duration-500"
                                            :class="isExpanded(section.id) ? `md:h-[10rem] h-[12rem] bg-[#D78924] text-white` : `h-[4rem]`"
                                        >
                                            <div class="flex items-start md:gap-6 gap-1">
                                                <div class="md:w-6 w-5">
                                                    <PlusIcon
                                                        v-if="!isExpanded(section.id)"
                                                        class="md:w-6 w-5"
                                                    />
                                                    <CloseIcon
                                                       v-if="isExpanded(section.id)"
                                                        class="md:w-6 w-5"
                                                    />
                                                </div>
                                                <div class="flex flex-col md:gap-4 gap-2">
                                                    <span class="font-bold md:text-xl leading-tight">
                                                        {{ section.name }}
                                                    </span>
                                                    <span
                                                        class="text-justify transition-all duration-500"
                                                        :class="isExpanded(section.id) ? `opacity-1 pointer-events-auto` : `opacity-0 pointer-events-none`"
                                                    >
                                                        {{ section.description }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Less hassle -->
        <div class="flex justify-center text-center w-full md:py-[2.5rem] p-[1rem]">
            <div class="flex flex-col items-center w-full md:max-w-[80%]">
                <h1 class="font-extrabold leading-tight lg:text-[45px] md:text-[40px] text-[26px] bg-gradient-to-r from-[#E37C0D] to-[#F2CD59] bg-clip-text text-transparent">
                    Less hassle, More hustle: <br>Sync Smicos with your favorite apps.
                </h1>
                <div class="md:max-w-[45%] w-full md:py-[2.5rem] py-[1rem]">
                    <img
                        :src="`/images/solutions/integration-icons.png`"
                        class="w-full"
                        alt="integration icons"
                    >
                </div>
                <!-- <div class="flex items-center justify-center py-10">
                    <div class="relative flex flex-wrap items-center justify-center md:ml-[5.75rem]">
                        <div 
                            v-for="integration in Integrations"
                            :key="integration.id"
                            :class="[`-left-${(integration.id - 1) * 4}`, `relative border rounded-full shadow-xl bg-white max-sm:left-0`]"
                        >
                            <div class="border-[5px] border-white rounded-full">
                                <component :is="integration.icon" class="border rounded-full" />
                            </div>
                        </div>
                    </div>
                </div> -->
                <a
                    href="https://app.smicos.com/register"
                    class="flex items-center gap-4 bg-smicos-secondary py-3 px-10 rounded-full text-white font-semibold"
                    target="_blank"
                >
                    <span>
                        Discover Integrations 
                    </span>
                    <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.7959 13.2959L10.2959 20.7959C10.0846 21.0072 9.79791 21.126 9.49902 21.126C9.20014 21.126 8.91349 21.0072 8.70215 20.7959C8.4908 20.5846 8.37207 20.2979 8.37207 19.999C8.37207 19.7001 8.4908 19.4135 8.70215 19.2021L15.4062 12.5L8.70402 5.7959C8.59937 5.69125 8.51636 5.56702 8.45973 5.43029C8.40309 5.29356 8.37394 5.14702 8.37394 4.99902C8.37394 4.85103 8.40309 4.70448 8.45973 4.56776C8.51636 4.43103 8.59937 4.30679 8.70402 4.20215C8.80867 4.0975 8.9329 4.01449 9.06963 3.95785C9.20636 3.90122 9.3529 3.87207 9.5009 3.87207C9.64889 3.87207 9.79543 3.90122 9.93216 3.95785C10.0689 4.01449 10.1931 4.0975 10.2978 4.20215L17.7978 11.7021C17.9025 11.8068 17.9856 11.9311 18.0422 12.0679C18.0988 12.2047 18.1279 12.3513 18.1277 12.4994C18.1275 12.6475 18.0981 12.7941 18.0412 12.9307C17.9843 13.0674 17.9009 13.1915 17.7959 13.2959Z" fill="white"/>
                    </svg>
                </a>
            </div>
        </div>
    </div>
</template>

<style scoped>
.bg-orange-bullets{
    box-shadow: 0 0 90px 100px rgb(227, 124, 13, 1);
}

.bg-blue-bullets{
    box-shadow: 0 0 90px 100px rgba(0, 174, 238, 1);
}

.section1-border-gradient {
    border-image-slice: 1;
    border-image-source: linear-gradient(to right, #F2CD59, #E37C0D);
}

.section2-border-gradient {
    border-image-slice: 1;
    border-image-source: linear-gradient(to right, #E37C0D, #E1251B);
}

.section3-border-gradient {
    border-image-slice: 1;
    border-image-source: linear-gradient(to right, #0A9EDF, #384590);
}

.section4-border-gradient {
    border-image-slice: 1;
    border-image-source: linear-gradient(to right, #E1251B, #E37C0D);
}
</style>
